/**
 * Who sees which line of this work's history, and which fields of it — OND-79.
 *
 * A LIST OF RULES AND ONE FORM, which is the right shape for the same reason
 * `WorkVisibilityPanel` beside it is a radio group and `WorkEstatePanel` is two
 * switches: the shape of the thing decides the shape of the control. A ceiling
 * has exactly one rung in force; the estate has two independent switches; this
 * has an unbounded set of rules at two grains, so it is a list the owner adds
 * to and removes from.
 *
 * IT SHOWS WHAT WAS WRITTEN AND NEVER WHAT IT MEANS. There is no "effective"
 * column and no preview of what a collector would see: the evaluation order
 * lives in `ondo.resolve_entry_disclosures()`, and a panel that computed it
 * would be a second copy of the one rule a mistake here discloses a sale price
 * through. What it does show is which rules exist, in the order they apply.
 *
 * INHERITANCE IS A LINE AND NOT A ROW, because inheritance is the ABSENCE of a
 * rule. An audience with no override is named under the list, so removing an
 * override visibly moves it from the rows into that line — which is the
 * ticket's "removing an override restores that inheritance", drawn.
 *
 * It renders nothing for a caller who may not manage the rules: the controller
 * hands it null, because `GET /disclosure` refuses a reader who is neither the
 * owner nor an agent on the work. There is no panel rather than a panel drawn
 * disabled — and `canManage` still gates the FORM, because an agent may read
 * the rules and might not be able to write one.
 */
import { WORK_DISCLOSURE_COPY } from "./work.constants.ts";
import type { DisclosurePanel } from "./disclosure.model.ts";
import type { DisclosureHandlers, RuleDraft } from "./useWorkDisclosure.ts";
import type { ProvenanceEntry } from "@ondo/shared";

import "./work.css";

type Props = {
  panel: DisclosurePanel | null;
  draft: RuleDraft;
  entries: ProvenanceEntry[];
  busy: boolean;
  failed: boolean;
  handlers: DisclosureHandlers;
};

export function WorkDisclosurePanel({
  panel,
  draft,
  entries,
  busy,
  failed,
  handlers,
}: Props) {
  if (panel === null) return null;

  return (
    <section
      className="work__panel work-disclosure"
      aria-labelledby="work-disclosure-heading"
    >
      <h3 className="work__panel-head" id="work-disclosure-heading">
        {WORK_DISCLOSURE_COPY.heading}
      </h3>
      <p className="work-disclosure__lead">{panel.lead}</p>

      {panel.rows.length === 0 ? (
        <p className="work-disclosure__empty">{WORK_DISCLOSURE_COPY.empty}</p>
      ) : (
        <ul className="work-disclosure__rules">
          {panel.rows.map((row) => (
            <li className="work-disclosure__rule" key={row.ruleId}>
              <span className="work-disclosure__rule-who">{row.heading}</span>
              <span className="work-disclosure__rule-scope">{row.scope}</span>
              <span className="work-disclosure__rule-what">{row.summary}</span>
              {/* Marked rather than hidden: an agent's rules outlive the
                  revocation of their mandate, so the owner has to be able to
                  see which are whose in order to undo them. */}
              {row.byAgent && (
                <span className="work-disclosure__rule-agent">
                  {WORK_DISCLOSURE_COPY.agentTag}
                </span>
              )}
              {row.canRemove && (
                <button
                  type="button"
                  className="work-disclosure__remove"
                  disabled={busy}
                  onClick={() => handlers.onRemove(row.ruleId)}
                >
                  {WORK_DISCLOSURE_COPY.remove}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {panel.inheriting.length > 0 && (
        <p className="work-disclosure__inheriting">
          {WORK_DISCLOSURE_COPY.inheritingLead}{" "}
          {panel.inheriting.map((each) => each.label).join(", ")}
        </p>
      )}

      {/* A COUNT AND NEVER A NAME. Who represents the work is the agents
          panel's disclosure, already made under the id-space rule; what this
          line says is that somebody may narrow these rules, which the owner is
          entitled to know on the panel the rules are on. */}
      {panel.delegates > 0 && (
        <p className="work-disclosure__delegated">
          {panel.delegates === 1
            ? WORK_DISCLOSURE_COPY.delegated
            : WORK_DISCLOSURE_COPY.delegatedMany}
        </p>
      )}

      {panel.canManage && (
        <form
          className="work-disclosure__add"
          onSubmit={(event) => {
            event.preventDefault();
            handlers.onSave();
          }}
        >
          <h4 className="work-disclosure__add-head">
            {WORK_DISCLOSURE_COPY.addHeading}
          </h4>

          <label className="work-disclosure__field">
            <span>{WORK_DISCLOSURE_COPY.audienceLabel}</span>
            {/* The empty value is the DEFAULT and is first, because it is the
                rule every audience inherits and the one an owner writes first.
                It is a real option rather than a placeholder: "everyone with
                access" is a thing the owner is choosing, not a thing they have
                not chosen yet. */}
            <select
              value={draft.audience ?? ""}
              disabled={busy}
              onChange={(event) =>
                handlers.onSetAudience(
                  event.target.value === ""
                    ? null
                    : (event.target.value as NonNullable<RuleDraft["audience"]>),
                )
              }
            >
              <option value="">{WORK_DISCLOSURE_COPY.everyoneOption}</option>
              {panel.audiences.map((each) => (
                <option key={each.audience} value={each.audience}>
                  {each.label}
                </option>
              ))}
            </select>
          </label>

          <label className="work-disclosure__field">
            <span>{WORK_DISCLOSURE_COPY.entryLabel}</span>
            <select
              value={draft.entryId ?? ""}
              disabled={busy}
              onChange={(event) =>
                handlers.onSetEntry(
                  event.target.value === "" ? null : event.target.value,
                )
              }
            >
              <option value="">{WORK_DISCLOSURE_COPY.everyEntryOption}</option>
              {entries.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.kind} · {entry.startText ?? entry.createdAt.slice(0, 10)}
                </option>
              ))}
            </select>
          </label>

          <label className="work-disclosure__hide-all">
            <input
              type="checkbox"
              checked={draft.hidden}
              disabled={busy}
              onChange={(event) => handlers.onSetHidden(event.target.checked)}
            />
            <span>{WORK_DISCLOSURE_COPY.hideWholeLabel}</span>
          </label>

          {/* The field list is disabled when the whole line is withheld, because
              withholding one field of a line nobody sees is not a second
              choice — it is the same rule written twice, and offering it would
              invite the question of which one won. */}
          <fieldset className="work-disclosure__fields" disabled={busy || draft.hidden}>
            <legend>{WORK_DISCLOSURE_COPY.fieldsLabel}</legend>
            {panel.fields.map((choice) => (
              <label className="work-disclosure__field-choice" key={choice.field}>
                <input
                  type="checkbox"
                  checked={draft.hiddenFields.includes(choice.field)}
                  onChange={() => handlers.onToggleField(choice.field)}
                />
                <span>{choice.label}</span>
              </label>
            ))}
          </fieldset>

          <button type="submit" className="work-disclosure__save" disabled={busy}>
            {WORK_DISCLOSURE_COPY.save}
          </button>
        </form>
      )}

      {busy && (
        <p className="work-disclosure__state" role="status">
          {WORK_DISCLOSURE_COPY.saving}
        </p>
      )}
      {/* The failure says nothing has changed, which is true: the controller
          adopts only what the server sent back, so a refused write leaves every
          rule exactly where it was. */}
      {failed && !busy && (
        <p
          className="work-disclosure__state work-disclosure__state--failed"
          role="alert"
        >
          {WORK_DISCLOSURE_COPY.failed}
        </p>
      )}
    </section>
  );
}
